/**
 * Dynamic Zoom: a camera over the finished frame.
 *
 * The move is applied after the mockup is composed, as a sub-rectangle of the
 * rendered master — the template travels together with its inserted content.
 * An authored separate background can follow that camera, stay fixed, or follow
 * half its scale and position deltas. The same viewports drive preview/export.
 */

export type DynamicZoomMode = 'transitions' | 'animation';
export type DynamicZoomFraming = 'full' | 'crop';
export type DynamicZoomSpeed = 'fast' | 'medium' | 'slow';
export type DynamicZoomBackgroundMotion = 'stick' | 'static' | 'parallax';

export interface DynamicZoomKey {
  id: string;
  /** Seconds from the start of the clip. */
  time: number;
  /** Centre of the viewport, 0..1 across the composed frame. */
  x: number;
  y: number;
  /** 1 = whole frame, 2 = half of it. */
  zoom: number;
  framing: DynamicZoomFraming;
}

export interface DynamicZoomState {
  enabled: boolean;
  mode: DynamicZoomMode;
  /** Used only when the source is a still image; a video brings its own. */
  duration: number;
  speed: DynamicZoomSpeed;
  backgroundMotion: DynamicZoomBackgroundMotion;
  keys: DynamicZoomKey[];
}

export interface DynamicZoomViewport {
  x: number;
  y: number;
  zoom: number;
}

export const DYNAMIC_ZOOM_MIN = 1;
export const DYNAMIC_ZOOM_MAX = 2;
export const DYNAMIC_ZOOM_DEFAULT = 2;
export const DYNAMIC_ZOOM_IMAGE_DURATIONS = [5, 10, 15];

/** Seconds a single move takes, in Transitions mode. */
export const DYNAMIC_ZOOM_SPEED_SECONDS: Record<DynamicZoomSpeed, number> = {
  fast: 0.5,
  medium: 1,
  slow: 2
};

/** Nothing placed yet, and what a "full" key returns to: the whole frame. */
export const DYNAMIC_ZOOM_FULL_VIEWPORT: DynamicZoomViewport = { x: 0.5, y: 0.5, zoom: 1 };

export function createDynamicZoomState(): DynamicZoomState {
  return {
    enabled: false,
    mode: 'transitions',
    duration: 10,
    speed: 'medium',
    backgroundMotion: 'stick',
    keys: []
  };
}

export function normalizeBackgroundMotion(value: unknown): DynamicZoomBackgroundMotion {
  return value === 'static' || value === 'parallax' ? value : 'stick';
}

/** Camera deltas, not absolute scale: a 2x device means a 1.5x parallax background. */
export function getBackgroundViewport(
  viewport: DynamicZoomViewport, mode: DynamicZoomBackgroundMotion
): DynamicZoomViewport {
  const factor = mode === 'static' ? 0 : mode === 'parallax' ? 0.5 : 1;
  return {
    x: 0.5 + (viewport.x - 0.5) * factor,
    y: 0.5 + (viewport.y - 0.5) * factor,
    zoom: 1 + (viewport.zoom - 1) * factor
  };
}

export function createDynamicZoomKey(
  time: number,
  x: number,
  y: number,
  framing: DynamicZoomFraming = 'crop'
): DynamicZoomKey {
  return {
    id: `k${Math.random().toString(36).slice(2, 9)}`,
    time: Math.max(0, time),
    x: clamp(x, 0, 1),
    y: clamp(y, 0, 1),
    zoom: framing === 'full' ? 1 : DYNAMIC_ZOOM_DEFAULT,
    framing
  };
}

export function sortKeys(keys: DynamicZoomKey[]): DynamicZoomKey[] {
  return [...keys].sort((a, b) => a.time - b.time);
}

/** Half an output frame: only duplicate moments replace one another. */
export function getKeyMergeWindow(_duration: number): number {
  // Keys represent moments in the exported 30 FPS clip. Visual marker overlap
  // must not delete distinct moments in a long recording.
  return 1 / 60;
}

export function findKeyAtTime(keys: DynamicZoomKey[], time: number): DynamicZoomKey | null {
  return keys.reduce<DynamicZoomKey | null>((nearest, key) => {
    const distance = Math.abs(key.time - time);
    return distance <= getKeyMergeWindow(0) && (!nearest || distance < Math.abs(nearest.time - time))
      ? key
      : nearest;
  }, null);
}

export function moveViewport(viewport: DynamicZoomViewport, dx: number, dy: number): DynamicZoomViewport {
  const rect = getSourceRect(viewport, 1, 1);
  const half = rect.width / 2;
  return {
    x: clamp(rect.x + half + dx, half, 1 - half),
    y: clamp(rect.y + half + dy, half, 1 - half),
    zoom: 1 / rect.width
  };
}

export type DynamicZoomCorner = 'nw' | 'ne' | 'sw' | 'se';

/** Resize a square crop around its opposite corner, without moving that anchor. */
export function resizeViewport(
  viewport: DynamicZoomViewport,
  corner: DynamicZoomCorner,
  dx: number,
  dy: number
): DynamicZoomViewport {
  const rect = getSourceRect(viewport, 1, 1);
  const sx = corner.endsWith('e') ? 1 : -1;
  const sy = corner.startsWith('s') ? 1 : -1;
  const anchorX = sx > 0 ? rect.x : rect.x + rect.width;
  const anchorY = sy > 0 ? rect.y : rect.y + rect.height;
  const size = clamp(rect.width + (sx * dx + sy * dy) / 2, 1 / DYNAMIC_ZOOM_MAX, 1 / DYNAMIC_ZOOM_MIN);
  // Keep the opposite corner until an edge is reached, then slide the crop
  // inside the stage. This allows every crop to expand all the way to Full.
  return moveViewport({ x: anchorX + sx * size / 2, y: anchorY + sy * size / 2, zoom: 1 / size }, 0, 0);
}

/** Editing at a new time creates a key; editing an existing moment keeps its ID. */
export function upsertViewportKey(
  keys: DynamicZoomKey[], time: number, viewport: DynamicZoomViewport, duration: number
): { keys: DynamicZoomKey[]; key: DynamicZoomKey } {
  const at = clamp(time, 0, duration);
  const existing = findKeyAtTime(keys, at);
  const normalized = moveViewport(viewport, 0, 0);
  const full = normalized.zoom <= 1 + 1e-6;
  const key: DynamicZoomKey = {
    ...(existing ?? createDynamicZoomKey(at, normalized.x, normalized.y)),
    ...(full ? DYNAMIC_ZOOM_FULL_VIEWPORT : normalized),
    framing: full ? 'full' : 'crop'
  };
  return { keys: upsertKey(keys, key, duration), key };
}

/**
 * Places a key, replacing anything already sitting at that moment.
 *
 * Stacked keys are invisible: the top one hides the rest, so the timeline shows
 * one marker while the camera obeys several, and deleting the visible one makes
 * a key the author never knew about spring back. The newcomer wins, which is
 * what re-aiming at the same moment means.
 */
export function upsertKey(
  keys: DynamicZoomKey[],
  key: DynamicZoomKey,
  duration: number
): DynamicZoomKey[] {
  const window = getKeyMergeWindow(duration);
  const kept = keys.filter(
    (candidate) => candidate.id !== key.id && Math.abs(candidate.time - key.time) > window
  );
  return sortKeys([...kept, key]);
}

/**
 * Where the camera is at a given moment.
 *
 * Every key is an arrival: its authored pose is reached at its timestamp.
 * Transitions holds the previous pose, then moves for at most Move speed
 * seconds before the next key. Short gaps compress the complete move.
 *
 * Animation mode is the familiar pairing instead: the camera travels the whole
 * gap between two keys. Before the first key the starting pose is Full at 0;
 * a key at 0 sets the initial pose immediately. Nothing moves after the last key.
 */
export function getViewportAt(
  state: DynamicZoomState,
  timeSeconds: number,
  /** The actual clip length overrides the stored still-image duration. */
  timelineDuration: number = state.duration
): DynamicZoomViewport {
  const end = Number.isFinite(timelineDuration) && timelineDuration >= 0 ? timelineDuration : Infinity;
  const keys = sortKeys(state.keys.filter(key => Number.isFinite(key.time) && key.time >= 0 && key.time <= end));
  timeSeconds = clamp(Number.isFinite(timeSeconds) ? timeSeconds : 0, 0, end);
  if (keys.length === 0) {
    return { ...DYNAMIC_ZOOM_FULL_VIEWPORT };
  }

  // Find the next arrival. Exact key times return that key's pose as the
  // starting point of the following segment; duplicate times use the last key.
  let index = 0;
  while (index < keys.length && keys[index].time <= timeSeconds) {
    index += 1;
  }
  if (index === keys.length) return toViewport(keys[keys.length - 1]);
  const target = keys[index];
  const from = index > 0 ? toViewport(keys[index - 1]) : DYNAMIC_ZOOM_FULL_VIEWPORT;
  const previousTime = index > 0 ? keys[index - 1].time : 0;
  const move = Math.max(1e-6, DYNAMIC_ZOOM_SPEED_SECONDS[state.speed]);
  const start = state.mode === 'animation' ? previousTime : Math.max(previousTime, target.time - move);
  const duration = target.time - start;
  const progress = duration === 0 ? 1 : clamp((timeSeconds - start) / duration, 0, 1);
  return blend(from, toViewport(target), easeInOut(progress));
}

/**
 * The tightest the camera gets anywhere in the clip.
 *
 * Read off the keys rather than sampled over time, because `blend` walks
 * between two zoom values and never leaves the interval between them — no
 * moment can be tighter than the tightest key. The export uses this to decide
 * how much detail the composed frame has to hold: a move that only reaches
 * 1.3x does not need a frame built for 2x.
 */
export function getMaxZoom(state: DynamicZoomState): number {
  return state.keys.reduce(
    (peak, key) => (key.framing === 'full' ? peak : Math.max(peak, clamp(key.zoom, 1, DYNAMIC_ZOOM_MAX))),
    1
  );
}

/**
 * The source rectangle to lift out of a composed frame.
 *
 * Kept inside the frame, so a centre near an edge slides the window rather than
 * sampling past it — a camera cannot see outside the picture.
 */
export function getSourceRect(
  viewport: DynamicZoomViewport,
  width: number,
  height: number
): { x: number; y: number; width: number; height: number } {
  const zoom = clamp(viewport.zoom, 1, DYNAMIC_ZOOM_MAX);
  const w = width / zoom;
  const h = height / zoom;
  const x = clamp(viewport.x * width - w / 2, 0, Math.max(0, width - w));
  const y = clamp(viewport.y * height - h / 2, 0, Math.max(0, height - h));
  return { x, y, width: w, height: h };
}

function toViewport(key: DynamicZoomKey): DynamicZoomViewport {
  return key.framing === 'full'
    ? { ...DYNAMIC_ZOOM_FULL_VIEWPORT }
    : { x: key.x, y: key.y, zoom: clamp(key.zoom, 1, DYNAMIC_ZOOM_MAX) };
}

/**
 * Zoom travels geometrically, position linearly.
 *
 * Interpolating zoom straight makes the move look like it slows down as it
 * closes in, because equal steps of scale are not equal steps of apparent
 * motion. Walking the logarithm keeps the push feeling even, which is what
 * "synchronised zoom and pan" has to mean to read as one camera move.
 */
function blend(from: DynamicZoomViewport, to: DynamicZoomViewport, t: number): DynamicZoomViewport {
  const amount = clamp(t, 0, 1);
  return {
    x: from.x + (to.x - from.x) * amount,
    y: from.y + (to.y - from.y) * amount,
    zoom: Math.exp(Math.log(from.zoom) + (Math.log(to.zoom) - Math.log(from.zoom)) * amount)
  };
}

/** Soft on both ends — the move never starts or stops abruptly. */
export function easeInOut(t: number): number {
  const amount = clamp(t, 0, 1);
  return amount < 0.5 ? 4 * amount * amount * amount : 1 - Math.pow(-2 * amount + 2, 3) / 2;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
